import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { linkPortal, gerarNovoTokenPortal } from '../lib/portal';

const COLUNAS_PADRAO = ['A fazer', 'Em andamento', 'Em revisão', 'Concluído'];

function iniciais(nome) { return (nome||'').split(' ').map(p=>p[0]).slice(0,2).join(''); }
const prioridadeConfig = {
  alta:  { cor: '#E05454', bg: '#FEF2F2', label: 'Alta' },
  media: { cor: '#C9A84C', bg: '#FFFBEB', label: 'Média' },
  baixa: { cor: '#5BAD8F', bg: '#F0FFF8', label: 'Baixa' },
};
const statusConfig = {
  'Ativo':    { cor: '#16a34a', bg: '#dcfce7' },
  'Pausado':  { cor: '#d97706', bg: '#fef3c7' },
  'Concluído':{ cor: '#6B2FA0', bg: '#f3e8ff' },
};
function hoje() { return new Date().toISOString().split('T')[0]; }
function agora() {
  const d = new Date();
  return `${d.toLocaleDateString('pt-BR')} às ${d.toLocaleTimeString('pt-BR', { hour:'2-digit', minute:'2-digit' })}`;
}
function diasRestantes(prazo) {
  if (!prazo) return null;
  return Math.ceil((new Date(prazo) - new Date()) / 86400000);
}

// Mapeia funis do colaborador → categorias de projeto que ele pode ver
function categoriasPermitidas(usuario) {
  if (!usuario || ['admin','gestor'].includes(usuario.perfil)) return null; // null = vê tudo
  const funis = usuario.funis || [];
  const cats = new Set();
  if (funis.some(f => f.startsWith('advocacia'))) cats.add('Advocacia');
  if (funis.includes('saude'))        cats.add('Saúde');
  if (funis.includes('infoprodutos')) cats.add('Infoprodutos');
  // Vuli sempre visível para quem tem acesso a projetos
  cats.add('Vuli');
  return cats.size > 0 ? cats : null;
}

export default function Projetos() {
  const { usuario } = useAuth();
  const navigate = useNavigate();
  const [projetos, setProjetos] = useState([]);
  const [tarefas, setTarefas] = useState([]);
  const [projetoAtivo, setProjetoAtivo] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [tarefaAberta, setTarefaAberta] = useState(null);
  const [modalProjeto, setModalProjeto] = useState(false);
  const [modalTarefa, setModalTarefa] = useState(false);
  const [modalColunas, setModalColunas] = useState(false);
  const [projetoEditando, setProjetoEditando] = useState(null);
  const [tarefaEditando, setTarefaEditando] = useState(null);
  const [dragId, setDragId] = useState(null);
  const [filtroResp, setFiltroResp] = useState('');
  const [filtroPrioridade, setFiltroPrioridade] = useState('');
  const [novaAtualizacao, setNovaAtualizacao] = useState('');
  const [novoLink, setNovoLink] = useState('');
  const [formProjeto, setFormProjeto] = useState({ nome:'', categoria:'Advocacia', cor:'#6B2FA0', status:'Ativo', descricao:'' });
  const [formTarefa, setFormTarefa] = useState({ titulo:'', status:'A fazer', responsaveis:[], prioridade:'media', prazo:'', atualizacoes:[], links:[], agendado:false, projeto_vinculado:'' });
  const [colunasEdit, setColunasEdit] = useState([]);
  const [novaColuna, setNovaColuna] = useState('');
  const [colaboradores, setColaboradores] = useState([]);
  const [modalPortal, setModalPortal] = useState(false);
  const [copiadoPortal, setCopiadoPortal] = useState(false);
  const [gerandoPortal, setGerandoPortal] = useState(false);
  const [verConcluidos, setVerConcluidos] = useState(false);
  const [modalHistorico, setModalHistorico] = useState(false);

  useEffect(() => { carregarProjetos(); carregarColaboradores(); }, []);

  async function carregarColaboradores() {
    const { data } = await supabase.from('colaboradores').select('nome, cor').eq('status', 'Ativo').order('nome');
    if (data) setColaboradores(data);
  }

  async function carregarProjetos() {
    setCarregando(true);
    const { data } = await supabase.from('projetos').select('*').order('nome');
    if (data && data.length > 0) {
      setProjetos(data);
      // Seleciona o primeiro projeto ativo (não concluído) que o usuário tem permissão de ver
      const cats = categoriasPermitidas(usuario);
      const permitido = (p) => !cats || cats.has(p.categoria);
      const primeiro = data.find(p => permitido(p) && p.status !== 'Concluído') || data.find(permitido);
      const projetoInicial = primeiro || data[0];
      setProjetoAtivo(projetoInicial);
      await carregarTarefas(projetoInicial.id);
    }
    setCarregando(false);
  }

  const [tarefasVinculadas, setTarefasVinculadas] = useState([]);

  async function carregarTarefas(projetoId) {
    const { data } = await supabase.from('tarefas').select('*').eq('projeto_id', projetoId).order('created_at');
    if (data) setTarefas(data);
  }

  async function carregarTarefasVinculadas(projetoNome) {
    const { data } = await supabase.from('tarefas').select('*').eq('projeto_vinculado', projetoNome).order('created_at');
    setTarefasVinculadas(data || []);
  }

  // Projetos filtrados pela categoria permitida do usuário + status (ativos vs concluídos)
  const catPermitidas = categoriasPermitidas(usuario);
  const projetosDaCategoria = catPermitidas
    ? projetos.filter(p => catPermitidas.has(p.categoria))
    : projetos;
  const projetosConcluidos = projetosDaCategoria.filter(p => p.status === 'Concluído');
  const projetosFiltrados = verConcluidos
    ? projetosConcluidos
    : projetosDaCategoria.filter(p => p.status !== 'Concluído');

  const ehAdmin = ['admin','gestor'].includes(usuario?.perfil);

  useEffect(() => {
    if (projetosFiltrados.length === 0) { setProjetoAtivo(null); return; }
    if (!projetosFiltrados.find(p => p.id === projetoAtivo?.id)) {
      selecionarProjeto(projetosFiltrados[0]);
    }
  }, [verConcluidos]);

  const colunasAtivas = projetoAtivo?.colunas || COLUNAS_PADRAO;

  async function selecionarProjeto(proj) {
    setProjetoAtivo(proj);
    await carregarTarefas(proj.id);
    if (proj.categoria !== 'Vuli') carregarTarefasVinculadas(proj.nome);
    else setTarefasVinculadas([]);
  }

  async function copiarLinkPortal() {
    if (!projetoAtivo?.portal_token) return;
    await navigator.clipboard.writeText(linkPortal(projetoAtivo.portal_token));
    setCopiadoPortal(true);
    setTimeout(() => setCopiadoPortal(false), 1500);
  }

  async function regenerarLinkPortal() {
    if (!projetoAtivo) return;
    if (!window.confirm('Isso invalida o link antigo — quem já tinha o link anterior perde o acesso. Continuar?')) return;
    setGerandoPortal(true);
    try {
      const novoToken = await gerarNovoTokenPortal(projetoAtivo.id);
      const atualizado = { ...projetoAtivo, portal_token: novoToken };
      setProjetoAtivo(atualizado);
      setProjetos(projetos.map(p => p.id === atualizado.id ? atualizado : p));
    } catch (e) {
      alert('Erro ao gerar novo link: ' + e.message);
    }
    setGerandoPortal(false);
  }

  async function salvarColunas(novasColunas) {
    if (!projetoAtivo) return;
    const { data } = await supabase.from('projetos').update({ colunas: novasColunas }).eq('id', projetoAtivo.id).select().single();
    if (data) {
      setProjetos(projetos.map(p => p.id === projetoAtivo.id ? data : p));
      setProjetoAtivo(data);
    }
    setModalColunas(false);
  }

  const tarefasFiltradas = tarefas.filter(t => {
    const respOk = !filtroResp || (t.responsaveis||[]).includes(filtroResp);
    const prioOk = !filtroPrioridade || t.prioridade === filtroPrioridade;
    // No projeto Vuli, não-admin só vê tarefas atribuídas a si
    const acessoOk = ehAdmin || projetoAtivo?.categoria !== 'Vuli' || (t.responsaveis||[]).includes(usuario?.nome);
    return respOk && prioOk && acessoOk;
  });

  async function salvarProjeto() {
    if (!formProjeto.nome) return;
    let payload = { ...formProjeto };
    if (projetoEditando && projetoEditando.status !== formProjeto.status &&
        (projetoEditando.status === 'Concluído' || formProjeto.status === 'Concluído')) {
      const acao = formProjeto.status === 'Concluído' ? 'Marcado como Concluído' : `Reativado (status: ${formProjeto.status})`;
      payload.log_status = [...(projetoEditando.log_status || []), { texto: acao, autor: usuario?.nome || '—', data_hora: agora() }];
    }
    if (projetoEditando) {
      const { data } = await supabase.from('projetos').update(payload).eq('id', projetoEditando.id).select().single();
      if (data) { setProjetos(projetos.map(p => p.id === projetoEditando.id ? data : p)); if (projetoAtivo?.id === projetoEditando.id) setProjetoAtivo(data); }
    } else {
      const { data } = await supabase.from('projetos').insert([payload]).select().single();
      if (data) { setProjetos([...projetos, data]); setProjetoAtivo(data); setTarefas([]); }
    }
    setModalProjeto(false); setProjetoEditando(null);
  }

  async function excluirProjeto(proj) {
    if (!window.confirm(`Excluir definitivamente o projeto "${proj.nome}"?\n\nIsso não pode ser desfeito. Se houver casos, tarefas ou conteúdos vinculados a ele, a exclusão vai ser bloqueada — mova ou exclua esses itens primeiro.`)) return;
    const { error } = await supabase.from('projetos').delete().eq('id', proj.id);
    if (error) {
      const bloqueado = error.code === '23503' || /foreign key|violates/i.test(error.message || '');
      alert(bloqueado
        ? 'Não foi possível excluir: ainda existem casos, tarefas ou conteúdos vinculados a esse projeto. Mova ou exclua esses itens primeiro.'
        : `Não foi possível excluir: ${error.message}`);
      return;
    }
    const restantes = projetos.filter(p => p.id !== proj.id);
    setProjetos(restantes);
    if (projetoAtivo?.id === proj.id) {
      const proximo = restantes.find(p => p.status !== 'Concluído') || restantes[0] || null;
      setProjetoAtivo(proximo);
      if (proximo) await carregarTarefas(proximo.id);
    }
  }

  async function salvarTarefa() {
    if (!formTarefa.titulo || !projetoAtivo) return;
    if (tarefaEditando) {
      const { data } = await supabase.from('tarefas').update(formTarefa).eq('id', tarefaEditando.id).select().single();
      if (data) setTarefas(tarefas.map(t => t.id === tarefaEditando.id ? data : t));
    } else {
      const { data } = await supabase.from('tarefas').insert([{ ...formTarefa, projeto_id: projetoAtivo.id }]).select().single();
      if (data) setTarefas([...tarefas, data]);
    }
    setModalTarefa(false); setTarefaEditando(null);
    setFormTarefa({ titulo:'', status:'A fazer', responsaveis:[], prioridade:'media', prazo:'', atualizacoes:[], links:[], agendado:false, projeto_vinculado:'' });
  }

  async function moverTarefa(tarefaId, novoStatus) {
    const { data } = await supabase.from('tarefas').update({ status: novoStatus }).eq('id', tarefaId).select().single();
    if (data) { setTarefas(tarefas.map(t => t.id === tarefaId ? data : t)); if (tarefaAberta?.id === tarefaId) setTarefaAberta(data); }
  }

  async function adicionarAtualizacao(tarefaId) {
    if (!novaAtualizacao.trim()) return;
    const tarefa = tarefas.find(t => t.id === tarefaId);
    const texto = `${hoje()} — ${novaAtualizacao.trim()}`;
    const atualizacoes = [...(tarefa.atualizacoes||[]), texto];
    const { data } = await supabase.from('tarefas').update({ atualizacoes }).eq('id', tarefaId).select().single();
    if (data) { setTarefas(tarefas.map(t => t.id === tarefaId ? data : t)); setTarefaAberta(data); }
    setNovaAtualizacao('');
  }

  async function adicionarLink(tarefaId) {
    if (!novoLink.trim()) return;
    const tarefa = tarefas.find(t => t.id === tarefaId);
    const links = [...(tarefa.links||[]), novoLink.trim()];
    const { data } = await supabase.from('tarefas').update({ links }).eq('id', tarefaId).select().single();
    if (data) { setTarefas(tarefas.map(t => t.id === tarefaId ? data : t)); setTarefaAberta(data); }
    setNovoLink('');
  }

  async function agendarTarefa(tarefaId) {
    const { data } = await supabase.from('tarefas').update({ agendado: true }).eq('id', tarefaId).select().single();
    if (data) { setTarefas(tarefas.map(t => t.id === tarefaId ? data : t)); setTarefaAberta(data); }
  }

  function toggleResponsavel(nome) {
    const atual = formTarefa.responsaveis||[];
    setFormTarefa({ ...formTarefa, responsaveis: atual.includes(nome) ? atual.filter(r=>r!==nome) : [...atual, nome] });
  }

  const corResp = (nome) => colaboradores.find(r=>r.nome===nome)?.cor||'#ccc';
  const inicialResp = (nome) => iniciais(nome) || (nome||'?')[0];

  if (carregando) return <div style={{ padding:40, textAlign:'center', color:'var(--gray-400)' }}>Carregando projetos...</div>;

  return (
    <div>
      <div className="section-header"><h2 className="section-title">Projetos</h2><div className="section-line" /></div>

      <div style={{ display:'flex', gap:8, marginBottom:20, flexWrap:'wrap', alignItems:'center', background:'white', borderRadius:14, padding:'10px 14px', border:'1px solid rgba(107,33,168,0.09)', boxShadow:'0 2px 10px rgba(63,0,81,0.05)' }}>
        {projetosFiltrados.map(p => (
          <button key={p.id} onClick={() => selecionarProjeto(p)}
            style={{ padding:'7px 16px', borderRadius:10, border:`1.5px solid ${projetoAtivo?.id===p.id ? p.cor : 'rgba(107,33,168,0.1)'}`, background:projetoAtivo?.id===p.id ? `linear-gradient(135deg,${p.cor},${p.cor}cc)` : 'transparent', color:projetoAtivo?.id===p.id?'white':'#7B5EA7', cursor:'pointer', fontWeight:projetoAtivo?.id===p.id?700:400, fontSize:12.5, fontFamily:'Poppins,sans-serif', transition:'all 0.18s', boxShadow:projetoAtivo?.id===p.id?`0 4px 14px ${p.cor}44`:'none' }}>
            {p.nome}
          </button>
        ))}
        {projetosFiltrados.length === 0 && (
          <span style={{ fontSize:12.5, color:'var(--gray-400)', padding:'7px 4px' }}>
            {verConcluidos ? 'Nenhum projeto concluído.' : 'Nenhum projeto ativo.'}
          </span>
        )}
        {ehAdmin && (
          <button onClick={() => { setProjetoEditando(null); setFormProjeto({ nome:'', categoria:'Advocacia', cor:'#6B2FA0', status:'Ativo', descricao:'' }); setModalProjeto(true); }}
            style={{ padding:'7px 16px', borderRadius:10, border:'1.5px dashed rgba(107,33,168,0.2)', background:'transparent', color:'#C084FC', cursor:'pointer', fontSize:12.5, fontFamily:'Poppins,sans-serif' }}>
            + Novo Projeto
          </button>
        )}
        <div style={{ flex:1 }} />
        <button onClick={() => setVerConcluidos(v => !v)}
          style={{ padding:'7px 16px', borderRadius:10, border:`1.5px solid ${verConcluidos ? '#6B2FA0' : 'rgba(107,33,168,0.15)'}`, background:verConcluidos ? '#f3e8ff' : 'transparent', color:'#6B2FA0', cursor:'pointer', fontSize:12.5, fontWeight:600, fontFamily:'Poppins,sans-serif' }}>
          {verConcluidos ? '← Ver ativos' : `🗄 Concluídos (${projetosConcluidos.length})`}
        </button>
      </div>

      {projetoAtivo && (
        <>
          <div className="card" style={{ padding:20, marginBottom:16, borderLeft:`4px solid ${projetoAtivo.cor}` }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
              <div>
                <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:6 }}>
                  <h3 style={{ margin:0, fontSize:18, color:'var(--text-primary)' }}>{projetoAtivo.nome}</h3>
                  <span style={{ background:statusConfig[projetoAtivo.status]?.bg, color:statusConfig[projetoAtivo.status]?.cor, padding:'3px 10px', borderRadius:20, fontSize:11, fontWeight:700 }}>{projetoAtivo.status}</span>
                  <span style={{ background:'var(--bg-secondary)', color:'var(--gray-400)', padding:'3px 10px', borderRadius:20, fontSize:11 }}>{projetoAtivo.categoria}</span>
                </div>
                {projetoAtivo.descricao && <div style={{ fontSize:13, color:'var(--gray-400)' }}>{projetoAtivo.descricao}</div>}
              </div>
              <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                <button onClick={() => navigate(`/roteiros-cliente/${projetoAtivo.id}`)}
                  style={{ background:'transparent', border:'1px solid var(--border)', padding:'6px 14px', borderRadius:6, cursor:'pointer', fontSize:12, color:'var(--text-secondary)' }}>
                  🎬 Roteiros do cliente
                </button>
                <button onClick={() => navigate(`/relatorio-cliente/${projetoAtivo.id}`)}
                  style={{ background:'transparent', border:'1px solid var(--border)', padding:'6px 14px', borderRadius:6, cursor:'pointer', fontSize:12, color:'var(--text-secondary)' }}>
                  📊 Relatório
                </button>
                <button onClick={() => navigate(`/playbook-cliente/${projetoAtivo.id}`)}
                  style={{ background:'transparent', border:'1px solid var(--border)', padding:'6px 14px', borderRadius:6, cursor:'pointer', fontSize:12, color:'var(--text-secondary)' }}>
                  📋 Playbook do cliente
                </button>
                <button onClick={() => setModalPortal(true)}
                  style={{ background:'transparent', border:'1px solid var(--border)', padding:'6px 14px', borderRadius:6, cursor:'pointer', fontSize:12, color:'var(--text-secondary)' }}>
                  🔗 Portal do cliente
                </button>
                <button onClick={() => { setProjetoEditando(projetoAtivo); setFormProjeto({ nome:projetoAtivo.nome, categoria:projetoAtivo.categoria, cor:projetoAtivo.cor, status:projetoAtivo.status, descricao:projetoAtivo.descricao }); setModalProjeto(true); }}
                  style={{ background:'transparent', border:'1px solid var(--border)', padding:'6px 14px', borderRadius:6, cursor:'pointer', fontSize:12, color:'var(--text-secondary)' }}>
                  Editar projeto
                </button>
                {(projetoAtivo.log_status || []).length > 0 && (
                  <button onClick={() => setModalHistorico(true)}
                    style={{ background:'transparent', border:'1px solid var(--border)', padding:'6px 14px', borderRadius:6, cursor:'pointer', fontSize:12, color:'var(--text-secondary)' }}>
                    📜 Histórico
                  </button>
                )}
                {ehAdmin && (
                  <button onClick={() => excluirProjeto(projetoAtivo)}
                    style={{ background:'transparent', border:'1px solid rgba(220,38,38,0.3)', padding:'6px 14px', borderRadius:6, cursor:'pointer', fontSize:12, color:'#dc2626' }}>
                    🗑 Excluir
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="filter-bar">
            <select value={filtroResp} onChange={e => setFiltroResp(e.target.value)} className={`chip-select${filtroResp ? ' active' : ''}`}>
              <option value=''>Responsável</option>
              {colaboradores.map(r => <option key={r.nome} value={r.nome}>{r.nome}</option>)}
            </select>
            <select value={filtroPrioridade} onChange={e => setFiltroPrioridade(e.target.value)} className={`chip-select${filtroPrioridade ? ' active' : ''}`}>
              <option value=''>Prioridade</option>
              {Object.entries(prioridadeConfig).map(([k,v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
            <button onClick={() => { setFiltroResp(''); setFiltroPrioridade(''); }}
              style={{ padding:'7px 12px', border:'none', borderRadius:10, background:'#F3EBFF', color:'#7B2FBE', cursor:'pointer', fontSize:12, fontWeight:600, fontFamily:'Poppins,sans-serif', display: filtroResp||filtroPrioridade ? 'block' : 'none' }}>
              Limpar
            </button>
            <div style={{ flex:1 }} />
            <button onClick={() => { setColunasEdit([...colunasAtivas]); setModalColunas(true); }}
              style={{ background:'white', border:'1.5px solid rgba(107,33,168,0.15)', padding:'8px 16px', borderRadius:10, cursor:'pointer', fontSize:12, color:'#7B5EA7', fontFamily:'Poppins,sans-serif', fontWeight:500 }}>
              Editar colunas
            </button>
            <button onClick={() => { setTarefaEditando(null); setFormTarefa({ titulo:'', status:colunasAtivas[0]||'A fazer', responsaveis:[], prioridade:'media', prazo:'', atualizacoes:[], links:[], agendado:false }); setModalTarefa(true); }}
              style={{ background:`linear-gradient(135deg,${projetoAtivo.cor},${projetoAtivo.cor}cc)`, color:'white', border:'none', padding:'8px 20px', borderRadius:10, fontWeight:700, cursor:'pointer', fontSize:13, fontFamily:'Poppins,sans-serif', boxShadow:`0 4px 14px ${projetoAtivo.cor}55` }}>
              + Nova Tarefa
            </button>
          </div>

          <div style={{ display:'grid', gridTemplateColumns:`repeat(${colunasAtivas.length},1fr)`, gap:14 }}>
            {colunasAtivas.map(coluna => {
              const cards = tarefasFiltradas.filter(t => t.status === coluna);
              return (
                <div key={coluna} onDragOver={e => e.preventDefault()} onDrop={() => { if (dragId) moverTarefa(dragId, coluna); setDragId(null); }}
                  style={{ background:'var(--bg-secondary)', borderRadius:10, padding:12, minHeight:200, border:dragId?`2px dashed ${projetoAtivo.cor}`:'2px solid transparent' }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
                    <span style={{ fontSize:11, fontWeight:700, color:'var(--text-secondary)', textTransform:'uppercase' }}>{coluna}</span>
                    <span style={{ background:projetoAtivo.cor, color:'white', borderRadius:20, padding:'2px 7px', fontSize:10, fontWeight:700 }}>{cards.length}</span>
                  </div>
                  <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                    {cards.map(tarefa => {
                      const pc = prioridadeConfig[tarefa.prioridade]||prioridadeConfig.media;
                      const dias = diasRestantes(tarefa.prazo);
                      const atrasada = dias!==null && dias<0;
                      return (
                        <div key={tarefa.id} draggable onDragStart={() => setDragId(tarefa.id)} onDragEnd={() => setDragId(null)}
                          onClick={() => setTarefaAberta(tarefa)} className="card"
                          style={{ padding:12, cursor:'grab', borderLeft:`3px solid ${projetoAtivo.cor}`, opacity:dragId===tarefa.id?0.4:1 }}>
                          <div style={{ fontWeight:600, fontSize:12, color:'var(--text-primary)', marginBottom:8 }}>{tarefa.titulo}</div>
                          <div style={{ display:'flex', gap:4, marginBottom:8 }}>
                            {(tarefa.responsaveis||[]).map(r => (
                              <div key={r} title={r} style={{ width:22, height:22, borderRadius:'50%', background:corResp(r), display:'flex', alignItems:'center', justifyContent:'center', fontSize:9, fontWeight:700, color:'white' }}>{inicialResp(r)}</div>
                            ))}
                          </div>
                          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                            <span style={{ background:pc.bg, color:pc.cor, padding:'2px 7px', borderRadius:20, fontSize:10, fontWeight:700 }}>{pc.label}</span>
                            {tarefa.prazo && <span style={{ fontSize:10, fontWeight:600, color:atrasada?'#E05454':dias<=3?'#C9A84C':'var(--gray-400)' }}>{atrasada?`${Math.abs(dias)}d atrás`:dias===0?'Hoje!':`${dias}d`}</span>}
                          </div>
                        </div>
                      );
                    })}
                    {cards.length===0 && <div style={{ textAlign:'center', padding:'16px 0', fontSize:11, color:`${projetoAtivo.cor}66` }}>Arraste aqui</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Tarefas da Vuli vinculadas a este projeto */}
      {projetoAtivo && projetoAtivo.categoria !== 'Vuli' && tarefasVinculadas.length > 0 && (
        <div style={{ marginTop:32 }}>
          <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:14 }}>
            <span style={{ fontWeight:700, fontSize:14, color:'#6B2FA0' }}>🔗 Atividades da Vuli vinculadas a este projeto</span>
            <div style={{ flex:1, height:1, background:'rgba(107,33,168,0.15)' }} />
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
            {tarefasVinculadas
              .filter(t => (t.responsaveis||[]).includes(usuario?.nome) || ehAdmin)
              .map(t => (
                <div key={t.id} className="card" style={{ padding:'12px 16px', display:'flex', alignItems:'center', gap:12, borderLeft:'3px solid #6B2FA0' }}>
                  <div style={{ flex:1 }}>
                    <div style={{ fontWeight:600, fontSize:13, color:'var(--text-primary)' }}>{t.titulo}</div>
                    {t.prazo && <div style={{ fontSize:11, color:'var(--gray-400)', marginTop:2 }}>Prazo: {t.prazo}</div>}
                  </div>
                  <span style={{ fontSize:11, fontWeight:700, padding:'3px 10px', borderRadius:20,
                    background: t.status==='Concluído'?'#dcfce7':t.status==='Em andamento'?'#e0f2fe':'#f3e8ff',
                    color: t.status==='Concluído'?'#16a34a':t.status==='Em andamento'?'#0891b2':'#6B2FA0' }}>
                    {t.status}
                  </span>
                </div>
              ))}
          </div>
        </div>
      )}

      {projetos.length === 0 && !carregando && (
        <div className="card" style={{ padding:40, textAlign:'center', color:'var(--gray-400)' }}>
          Nenhum projeto cadastrado. Clique em "+ Novo Projeto" para começar!
        </div>
      )}

      {/* Painel lateral tarefa */}
      {tarefaAberta && (
        <div style={{ position:'fixed', inset:0, zIndex:1000, display:'flex' }}>
          <div style={{ flex:1, background:'rgba(0,0,0,0.4)' }} onClick={() => setTarefaAberta(null)} />
          <div style={{ width:520, background:'#ffffff', borderLeft:'1px solid var(--border)', overflowY:'auto', padding:28, boxShadow:'-4px 0 24px rgba(0,0,0,0.15)' }}>
            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:20 }}>
              <h3 style={{ margin:0, color:'var(--text-primary)', fontSize:17, flex:1, marginRight:12 }}>{tarefaAberta.titulo}</h3>
              <div style={{ display:'flex', gap:8 }}>
                <button onClick={() => { setTarefaEditando(tarefaAberta); setFormTarefa({...tarefaAberta}); setModalTarefa(true); setTarefaAberta(null); }}
                  style={{ background:'var(--purple)', color:'white', border:'none', padding:'6px 12px', borderRadius:6, cursor:'pointer', fontSize:12, fontWeight:600 }}>Editar</button>
                <button onClick={() => setTarefaAberta(null)} style={{ background:'transparent', border:'none', fontSize:20, cursor:'pointer', color:'var(--gray-400)' }}>×</button>
              </div>
            </div>
            <div className="card" style={{ padding:16, marginBottom:14 }}>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                <div><div style={{ fontSize:10, color:'var(--gray-400)', marginBottom:2, textTransform:'uppercase', fontWeight:600 }}>Status</div><span style={{ fontSize:12, fontWeight:600, color:'var(--purple)' }}>{tarefaAberta.status}</span></div>
                <div><div style={{ fontSize:10, color:'var(--gray-400)', marginBottom:2, textTransform:'uppercase', fontWeight:600 }}>Prioridade</div><span style={{ fontSize:12, fontWeight:600, color:prioridadeConfig[tarefaAberta.prioridade]?.cor }}>{prioridadeConfig[tarefaAberta.prioridade]?.label}</span></div>
                <div><div style={{ fontSize:10, color:'var(--gray-400)', marginBottom:2, textTransform:'uppercase', fontWeight:600 }}>Prazo</div><span style={{ fontSize:12, color:diasRestantes(tarefaAberta.prazo)<0?'#E05454':'var(--text-primary)', fontWeight:600 }}>{tarefaAberta.prazo||'—'}</span></div>
                <div><div style={{ fontSize:10, color:'var(--gray-400)', marginBottom:2, textTransform:'uppercase', fontWeight:600 }}>Responsáveis</div><div style={{ display:'flex', gap:4 }}>{(tarefaAberta.responsaveis||[]).map(r => <div key={r} title={r} style={{ width:24, height:24, borderRadius:'50%', background:corResp(r), display:'flex', alignItems:'center', justifyContent:'center', fontSize:9, fontWeight:700, color:'white' }}>{inicialResp(r)}</div>)}</div></div>
              </div>
            </div>
            <div className="card" style={{ padding:14, marginBottom:14 }}>
              <div style={{ fontSize:10, fontWeight:600, color:'var(--gray-400)', marginBottom:8, textTransform:'uppercase' }}>Mover para</div>
              <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                {colunasAtivas.map(s => <button key={s} onClick={() => moverTarefa(tarefaAberta.id, s)} style={{ padding:'5px 12px', borderRadius:20, border:`1px solid ${tarefaAberta.status===s?projetoAtivo?.cor:'var(--border)'}`, background:tarefaAberta.status===s?projetoAtivo?.cor:'transparent', color:tarefaAberta.status===s?'white':'var(--text-secondary)', cursor:'pointer', fontSize:11, fontWeight:tarefaAberta.status===s?700:400 }}>{s}</button>)}
              </div>
            </div>
            <div className="card" style={{ padding:14, marginBottom:14 }}>
              <div style={{ fontSize:10, fontWeight:600, color:'var(--gray-400)', marginBottom:10, textTransform:'uppercase' }}>Atualizações</div>
              {(tarefaAberta.atualizacoes||[]).map((a,i) => <div key={i} style={{ padding:'8px 10px', background:'var(--bg-secondary)', borderRadius:6, marginBottom:6, fontSize:12, color:'var(--text-secondary)' }}>{a}</div>)}
              <div style={{ display:'flex', gap:8, marginTop:8 }}>
                <input placeholder="Adicionar atualização..." value={novaAtualizacao} onChange={e => setNovaAtualizacao(e.target.value)}
                  style={{ flex:1, padding:'7px 10px', border:'1px solid var(--border)', borderRadius:6, fontSize:12, background:'var(--bg-secondary)', color:'var(--text-primary)' }} />
                <button onClick={() => adicionarAtualizacao(tarefaAberta.id)} style={{ background:'var(--purple)', color:'white', border:'none', padding:'7px 12px', borderRadius:6, cursor:'pointer', fontSize:12 }}>+</button>
              </div>
            </div>
            <div className="card" style={{ padding:14, marginBottom:14 }}>
              <div style={{ fontSize:10, fontWeight:600, color:'var(--gray-400)', marginBottom:10, textTransform:'uppercase' }}>Links</div>
              {(tarefaAberta.links||[]).map((l,i) => <a key={i} href={l} target="_blank" rel="noreferrer" style={{ display:'block', fontSize:12, color:'#4A90D9', marginBottom:4, wordBreak:'break-all', textDecoration:'none' }}>🔗 {l}</a>)}
              <div style={{ display:'flex', gap:8, marginTop:8 }}>
                <input placeholder="Colar link..." value={novoLink} onChange={e => setNovoLink(e.target.value)}
                  style={{ flex:1, padding:'7px 10px', border:'1px solid var(--border)', borderRadius:6, fontSize:12, background:'var(--bg-secondary)', color:'var(--text-primary)' }} />
                <button onClick={() => adicionarLink(tarefaAberta.id)} style={{ background:'#4A90D9', color:'white', border:'none', padding:'7px 12px', borderRadius:6, cursor:'pointer', fontSize:12 }}>+</button>
              </div>
            </div>
            <div className="card" style={{ padding:14 }}>
              <div style={{ fontSize:10, fontWeight:600, color:'var(--gray-400)', marginBottom:10, textTransform:'uppercase' }}>Agenda</div>
              {tarefaAberta.agendado ? <div style={{ fontSize:12, color:'#4A90D9', fontWeight:600 }}>📅 Na agenda — prazo: {tarefaAberta.prazo}</div> :
                <button onClick={() => agendarTarefa(tarefaAberta.id)} style={{ background:'#4A90D9', color:'white', border:'none', padding:'8px 16px', borderRadius:6, cursor:'pointer', fontSize:12, fontWeight:600 }}>📅 Adicionar à agenda</button>}
            </div>
          </div>
        </div>
      )}

      {/* Modal projeto */}
      {modalProjeto && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }}>
          <div className="card" style={{ width:460, padding:32 }}>
            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:20 }}>
              <h3 style={{ margin:0, color:'var(--purple)' }}>{projetoEditando?'Editar Projeto':'Novo Projeto'}</h3>
              <button onClick={() => setModalProjeto(false)} style={{ background:'transparent', border:'none', fontSize:20, cursor:'pointer', color:'var(--gray-400)' }}>×</button>
            </div>
            {[{label:'Nome',key:'nome',type:'text'},{label:'Descrição',key:'descricao',type:'text'}].map(f => (
              <div key={f.key} style={{ marginBottom:14 }}>
                <label style={{ display:'block', fontSize:11, fontWeight:600, color:'var(--gray-400)', marginBottom:5, textTransform:'uppercase' }}>{f.label}</label>
                <input type={f.type} value={formProjeto[f.key]} onChange={e => setFormProjeto({...formProjeto,[f.key]:e.target.value})}
                  style={{ width:'100%', padding:'9px 12px', border:'1px solid var(--border)', borderRadius:6, fontSize:13, background:'var(--bg-secondary)', color:'var(--text-primary)', boxSizing:'border-box' }} />
              </div>
            ))}
            {[{label:'Categoria',key:'categoria',opts:['Advocacia','Saúde','Infoprodutos','Vuli','Outro']},{label:'Status',key:'status',opts:['Ativo','Pausado','Concluído']}].map(f => (
              <div key={f.key} style={{ marginBottom:14 }}>
                <label style={{ display:'block', fontSize:11, fontWeight:600, color:'var(--gray-400)', marginBottom:5, textTransform:'uppercase' }}>{f.label}</label>
                <select value={formProjeto[f.key]} onChange={e => setFormProjeto({...formProjeto,[f.key]:e.target.value})}
                  style={{ width:'100%', padding:'9px 12px', border:'1px solid var(--border)', borderRadius:6, fontSize:13, background:'var(--bg-secondary)', color:'var(--text-primary)' }}>
                  {f.opts.map(o => <option key={o}>{o}</option>)}
                </select>
              </div>
            ))}
            <div style={{ marginBottom:20 }}>
              <label style={{ display:'block', fontSize:11, fontWeight:600, color:'var(--gray-400)', marginBottom:8, textTransform:'uppercase' }}>Cor</label>
              <div style={{ display:'flex', gap:8 }}>
                {['#6B2FA0','#4A90D9','#D4789B','#C9A84C','#5BAD8F','#E05454','#9B7FD4'].map(cor => (
                  <div key={cor} onClick={() => setFormProjeto({...formProjeto,cor})}
                    style={{ width:28, height:28, borderRadius:'50%', background:cor, cursor:'pointer', border:formProjeto.cor===cor?'3px solid #333':'2px solid transparent' }} />
                ))}
              </div>
            </div>
            <div style={{ display:'flex', gap:10 }}>
              <button onClick={salvarProjeto} style={{ flex:1, background:'var(--purple)', color:'white', border:'none', padding:'11px', borderRadius:7, fontWeight:700, cursor:'pointer', fontSize:14 }}>Salvar</button>
              <button onClick={() => setModalProjeto(false)} style={{ flex:1, background:'transparent', border:'1px solid var(--border)', padding:'11px', borderRadius:7, cursor:'pointer', color:'var(--text-secondary)', fontSize:14 }}>Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal editar colunas */}
      {modalColunas && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }}>
          <div className="card" style={{ width:420, padding:32 }}>
            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:20 }}>
              <h3 style={{ margin:0, color:'var(--purple)' }}>Editar colunas do kanban</h3>
              <button onClick={() => setModalColunas(false)} style={{ background:'transparent', border:'none', fontSize:20, cursor:'pointer', color:'var(--gray-400)' }}>×</button>
            </div>
            <div style={{ marginBottom:16 }}>
              {colunasEdit.map((col, idx) => (
                <div key={idx} style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8 }}>
                  <span style={{ fontSize:13, color:'var(--gray-400)', width:20, textAlign:'center' }}>{idx+1}</span>
                  <input value={col} onChange={e => { const novo = [...colunasEdit]; novo[idx] = e.target.value; setColunasEdit(novo); }}
                    style={{ flex:1, padding:'8px 12px', border:'1px solid var(--border)', borderRadius:6, fontSize:13, background:'var(--bg-secondary)', color:'var(--text-primary)' }} />
                  <button onClick={() => setColunasEdit(colunasEdit.filter((_,i)=>i!==idx))}
                    disabled={colunasEdit.length <= 1}
                    style={{ background:'#fee2e2', border:'none', color:'#dc2626', borderRadius:6, width:30, height:30, cursor:'pointer', fontSize:16, fontWeight:700, opacity:colunasEdit.length<=1?0.4:1 }}>×</button>
                  {idx > 0 && <button onClick={() => { const n=[...colunasEdit]; [n[idx-1],n[idx]]=[n[idx],n[idx-1]]; setColunasEdit(n); }}
                    style={{ background:'var(--bg-secondary)', border:'1px solid var(--border)', borderRadius:6, width:30, height:30, cursor:'pointer', fontSize:12 }}>↑</button>}
                </div>
              ))}
            </div>
            <div style={{ display:'flex', gap:8, marginBottom:20 }}>
              <input placeholder="Nova coluna..." value={novaColuna} onChange={e => setNovaColuna(e.target.value)}
                onKeyDown={e => { if(e.key==='Enter'&&novaColuna.trim()){ setColunasEdit([...colunasEdit,novaColuna.trim()]); setNovaColuna(''); }}}
                style={{ flex:1, padding:'8px 12px', border:'1px solid var(--border)', borderRadius:6, fontSize:13, background:'var(--bg-secondary)', color:'var(--text-primary)' }} />
              <button onClick={() => { if(novaColuna.trim()){ setColunasEdit([...colunasEdit,novaColuna.trim()]); setNovaColuna(''); }}}
                style={{ background:'var(--purple)', color:'white', border:'none', padding:'8px 16px', borderRadius:6, cursor:'pointer', fontWeight:700 }}>+</button>
            </div>
            <div style={{ display:'flex', gap:10 }}>
              <button onClick={() => salvarColunas(colunasEdit)} style={{ flex:1, background:'var(--purple)', color:'white', border:'none', padding:'11px', borderRadius:7, fontWeight:700, cursor:'pointer', fontSize:14 }}>Salvar</button>
              <button onClick={() => { setColunasEdit(COLUNAS_PADRAO); }} style={{ padding:'11px 14px', background:'transparent', border:'1px solid var(--border)', borderRadius:7, cursor:'pointer', color:'var(--gray-400)', fontSize:12 }}>Resetar</button>
              <button onClick={() => setModalColunas(false)} style={{ flex:1, background:'transparent', border:'1px solid var(--border)', padding:'11px', borderRadius:7, cursor:'pointer', color:'var(--text-secondary)', fontSize:14 }}>Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal tarefa */}
      {modalTarefa && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }}>
          <div className="card" style={{ width:480, padding:32, maxHeight:'90vh', overflowY:'auto' }}>
            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:20 }}>
              <h3 style={{ margin:0, color:projetoAtivo?.cor||'var(--purple)' }}>{tarefaEditando?'Editar Tarefa':'Nova Tarefa'}</h3>
              <button onClick={() => setModalTarefa(false)} style={{ background:'transparent', border:'none', fontSize:20, cursor:'pointer', color:'var(--gray-400)' }}>×</button>
            </div>
            <div style={{ marginBottom:14 }}>
              <label style={{ display:'block', fontSize:11, fontWeight:600, color:'var(--gray-400)', marginBottom:5, textTransform:'uppercase' }}>Título</label>
              <input value={formTarefa.titulo} onChange={e => setFormTarefa({...formTarefa,titulo:e.target.value})}
                style={{ width:'100%', padding:'9px 12px', border:'1px solid var(--border)', borderRadius:6, fontSize:13, background:'var(--bg-secondary)', color:'var(--text-primary)', boxSizing:'border-box' }} />
            </div>
            <div style={{ marginBottom:14 }}>
              <label style={{ display:'block', fontSize:11, fontWeight:600, color:'var(--gray-400)', marginBottom:8, textTransform:'uppercase' }}>Responsáveis</label>
              <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
                {colaboradores.map(r => {
                  const ativo = (formTarefa.responsaveis||[]).includes(r.nome);
                  return <div key={r.nome} onClick={() => toggleResponsavel(r.nome)} style={{ display:'flex', alignItems:'center', gap:8, padding:'8px 14px', borderRadius:8, border:`2px solid ${ativo?r.cor:'var(--border)'}`, background:ativo?r.cor+'22':'transparent', cursor:'pointer' }}>
                    <div style={{ width:24, height:24, borderRadius:'50%', background:r.cor||'#ccc', display:'flex', alignItems:'center', justifyContent:'center', fontSize:10, fontWeight:700, color:'white' }}>{iniciais(r.nome)}</div>
                    <span style={{ fontSize:12, fontWeight:ativo?700:400, color:ativo?'var(--text-primary)':'var(--text-secondary)' }}>{r.nome.split(' ')[0]}</span>
                  </div>;
                })}
              </div>
            </div>
            {[{label:'Status',key:'status',opts:colunasAtivas},{label:'Prioridade',key:'prioridade',opts:['alta','media','baixa']}].map(f => (
              <div key={f.key} style={{ marginBottom:14 }}>
                <label style={{ display:'block', fontSize:11, fontWeight:600, color:'var(--gray-400)', marginBottom:5, textTransform:'uppercase' }}>{f.label}</label>
                <select value={formTarefa[f.key]} onChange={e => setFormTarefa({...formTarefa,[f.key]:e.target.value})}
                  style={{ width:'100%', padding:'9px 12px', border:'1px solid var(--border)', borderRadius:6, fontSize:13, background:'var(--bg-secondary)', color:'var(--text-primary)' }}>
                  {f.opts.map(o => <option key={o}>{o}</option>)}
                </select>
              </div>
            ))}
            <div style={{ marginBottom:14 }}>
              <label style={{ display:'block', fontSize:11, fontWeight:600, color:'var(--gray-400)', marginBottom:5, textTransform:'uppercase' }}>Prazo</label>
              <input type="date" value={formTarefa.prazo} onChange={e => setFormTarefa({...formTarefa,prazo:e.target.value})}
                style={{ width:'100%', padding:'9px 12px', border:'1px solid var(--border)', borderRadius:6, fontSize:13, background:'var(--bg-secondary)', color:'var(--text-primary)', boxSizing:'border-box' }} />
            </div>
            {projetoAtivo?.categoria === 'Vuli' && (
              <div style={{ marginBottom:14 }}>
                <label style={{ display:'block', fontSize:11, fontWeight:600, color:'var(--gray-400)', marginBottom:5, textTransform:'uppercase' }}>Compartilhar com projeto cliente</label>
                <select value={formTarefa.projeto_vinculado||''} onChange={e => setFormTarefa({...formTarefa,projeto_vinculado:e.target.value})}
                  style={{ width:'100%', padding:'9px 12px', border:'1px solid var(--border)', borderRadius:6, fontSize:13, background:'var(--bg-secondary)', color:'var(--text-primary)' }}>
                  <option value=''>— Apenas interno —</option>
                  {projetos.filter(p => p.categoria !== 'Vuli').map(p => <option key={p.id} value={p.nome}>{p.nome}</option>)}
                </select>
              </div>
            )}
            <div style={{ marginBottom:20 }}>
              <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer' }}>
                <input type="checkbox" checked={formTarefa.agendado} onChange={e => setFormTarefa({...formTarefa,agendado:e.target.checked})} />
                <span style={{ fontSize:13, color:'var(--text-secondary)' }}>📅 Adicionar à agenda</span>
              </label>
            </div>
            <div style={{ display:'flex', gap:10 }}>
              <button onClick={salvarTarefa} style={{ flex:1, background:projetoAtivo?.cor||'var(--purple)', color:'white', border:'none', padding:'11px', borderRadius:7, fontWeight:700, cursor:'pointer', fontSize:14 }}>Salvar</button>
              <button onClick={() => setModalTarefa(false)} style={{ flex:1, background:'transparent', border:'1px solid var(--border)', padding:'11px', borderRadius:7, cursor:'pointer', color:'var(--text-secondary)', fontSize:14 }}>Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Portal do Cliente */}
      {modalPortal && projetoAtivo && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1100 }}>
          <div className="card" style={{ width:460, padding:32 }}>
            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:6 }}>
              <h3 style={{ margin:0, color:'var(--purple)' }}>Portal do cliente</h3>
              <button onClick={() => setModalPortal(false)} style={{ background:'transparent', border:'none', fontSize:20, cursor:'pointer', color:'var(--gray-400)' }}>×</button>
            </div>
            <p style={{ fontSize:12.5, color:'var(--text-secondary)', marginBottom:18 }}>
              Link único e sem login pra {projetoAtivo.nome} ver casos, financeiro, conteúdo e acessos deste projeto.
            </p>
            <div style={{ display:'flex', gap:8, marginBottom:14 }}>
              <input readOnly value={projetoAtivo.portal_token ? linkPortal(projetoAtivo.portal_token) : 'gerando link...'}
                style={{ flex:1, padding:'9px 12px', border:'1px solid var(--border)', borderRadius:6, fontSize:12, background:'var(--bg-secondary)', color:'var(--text-primary)', fontFamily:'JetBrains Mono, monospace' }} />
              <button onClick={copiarLinkPortal} disabled={!projetoAtivo.portal_token}
                style={{ background:'var(--purple)', color:'white', border:'none', padding:'9px 16px', borderRadius:6, cursor:'pointer', fontSize:12, fontWeight:700, whiteSpace:'nowrap' }}>
                {copiadoPortal ? '✓ copiado' : 'Copiar'}
              </button>
            </div>
            {!projetoAtivo.portal_token && (
              <div style={{ fontSize:11.5, color:'#dc2626', marginBottom:14 }}>
                Este projeto ainda não tem um link — rode a migration <code>supabase_migrations_portal_cliente.sql</code> no Supabase pra gerar automaticamente.
              </div>
            )}
            <button onClick={regenerarLinkPortal} disabled={gerandoPortal}
              style={{ width:'100%', background:'transparent', border:'1px solid rgba(220,38,38,0.3)', color:'#dc2626', padding:'9px', borderRadius:7, cursor:'pointer', fontSize:12.5, fontWeight:600 }}>
              {gerandoPortal ? 'Gerando...' : '⟳ Gerar novo link (invalida o antigo)'}
            </button>
          </div>
        </div>
      )}

      {/* Modal Histórico de status */}
      {modalHistorico && projetoAtivo && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1100 }}>
          <div className="card" style={{ width:440, padding:32, maxHeight:'80vh', overflowY:'auto' }}>
            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:14 }}>
              <h3 style={{ margin:0, color:'var(--purple)' }}>Histórico — {projetoAtivo.nome}</h3>
              <button onClick={() => setModalHistorico(false)} style={{ background:'transparent', border:'none', fontSize:20, cursor:'pointer', color:'var(--gray-400)' }}>×</button>
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
              {(projetoAtivo.log_status || []).slice().reverse().map((l, i) => (
                <div key={i} style={{ padding:'10px 12px', background:'var(--bg-secondary)', borderRadius:8, fontSize:12.5, color:'var(--text-primary)' }}>
                  <div style={{ fontWeight:600 }}>{l.texto}</div>
                  <div style={{ fontSize:11, color:'var(--gray-400)', marginTop:2 }}>{l.data_hora}{l.autor ? ` · ${l.autor}` : ''}</div>
                </div>
              ))}
              {(projetoAtivo.log_status || []).length === 0 && (
                <div style={{ textAlign:'center', padding:20, color:'var(--gray-400)', fontSize:12.5 }}>Nenhum registro ainda.</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
